import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../../styles/Admin/LiveGoldRate.css';

const fmtNum = (n) => n != null ? Number(n).toLocaleString('en-IN') : '--';

const LiveGoldRate = () => {
  const [rateData, setRateData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get('/api/rates');
        const map = {};
        data.forEach(r => { map[r.metalType] = r; });
        setRateData(map);
        if (data.length) {
          const newest = new Date(Math.max(...data.map(r => new Date(r.lastUpdated).getTime())));
          setLastUpdated(newest);
        }
        setError(null);
      } catch (err) {
        setError('Unable to fetch live rates');
        console.error('Error fetching rates:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
    const interval = setInterval(fetchRates, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const gold24 = rateData['Gold 24K'];
  const gold22 = rateData['Gold 22K'];
  const silver = rateData['Silver'];

  // Build display-friendly objects for the UI below
  const rates = {
    gold995: { sell: gold24 ? fmtNum(gold24.rate) : '--', high: '--', low: '--' },
    silverChorsa: { sell: silver ? fmtNum(Math.round(silver.rate * 0.98)) : '--', high: '--', low: '--' },
    silverBank: { sell: silver ? fmtNum(silver.rate) : '--', high: '--', low: '--' },
    goldFuture: { bid: gold22 ? fmtNum(gold22.rate) : '--', ask: '--', high: '--', low: '--' },
    silverFuture: { bid: silver ? fmtNum(Math.round(silver.rate * 1.02)) : '--', ask: '--', high: '--', low: '--' },
  };

  const formatNumber = (num) => {
    // If num is already formatted with commas, return it
    if (typeof num === 'string' && num.includes(',')) return num;
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const getChangeColor = (change) => {
    if (!change) return '#aaa';
    return change.includes('+') ? '#00ff88' : '#ff4757';
  };

  if (loading) {
    return (
      <div className="live-rates-loading">
        <div className="loading-spinner"></div>
        <p>Connecting to Market...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="live-rates-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="live-gold-rate-container">
      {/* Header with Icons */}
      <div className="rates-header">
        <div className="header-left">
          <div className="header-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
            </svg>
          </div>
          <div className="header-text">
            <h1>Live Rates</h1>
            <p>Real-time Market Prices (INR)</p>
          </div>
        </div>
        <div className="header-right">
          <div className="last-updated">
            <span style={{ color: '#00ff88' }}>●</span>
            <span>{lastUpdated ? lastUpdated.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Loading…'}</span>
          </div>
        </div>
      </div>

      {/* Live Product Rates Section */}
      <div className="rates-section">
        <div className="rates-table">
          <div className="table-header">
            <div className="col-description">PRODUCT</div>
            <div className="col-buy">BUY</div>
            <div className="col-sell">SELL</div>
            <div className="col-change">CHANGE</div>
          </div>

          {/* Row 1: Gold */}
          <div className="table-row">
            <div className="col-description">
              <div className="product-info">
                <div className="product-icon">Au</div>
                <div className="product-details">
                  <div className="product-name">GOLD 995 JDR</div>
                  <div className="product-subtitle">Ready Stock (3% GST Paid)</div>
                </div>
              </div>
            </div>
            <div className="col-buy">
              <div className="price-value">{rates.gold995.buy}</div>
              <div className="price-subtitle">Low: {rates.gold995.low}</div>
            </div>
            <div className="col-sell">
              <div className="price-value sell-price">{rates.gold995.sell}</div>
              <div className="price-subtitle">High: {rates.gold995.high}</div>
            </div>
            <div className="col-change">
              <div className="change-value" style={{ color: getChangeColor(rates.gold995.change) }}>
                {rates.gold995.change}
              </div>
            </div>
          </div>

          {/* Row 2: Silver Chorsa */}
          <div className="table-row">
            <div className="col-description">
              <div className="product-info">
                <div className="product-icon silver">Ag</div>
                <div className="product-details">
                  <div className="product-name">SILVER CHORSA 98.00</div>
                  <div className="product-subtitle">Ready Stock (3% GST Paid)</div>
                </div>
              </div>
            </div>
            <div className="col-buy">
              <div className="price-value">{rates.silverChorsa.buy}</div>
              <div className="price-subtitle">Low: {rates.silverChorsa.low}</div>
            </div>
            <div className="col-sell">
              <div className="price-value sell-price">{rates.silverChorsa.sell}</div>
              <div className="price-subtitle">High: {rates.silverChorsa.high}</div>
            </div>
            <div className="col-change">
              <div className="change-value" style={{ color: getChangeColor(rates.silverChorsa.change) }}>
                {rates.silverChorsa.change}
              </div>
            </div>
          </div>

          {/* Row 3: Silver Bank */}
          <div className="table-row">
            <div className="col-description">
              <div className="product-info">
                <div className="product-icon silver">Ag</div>
                <div className="product-details">
                  <div className="product-name">SILVER BANK PETI 999.9</div>
                  <div className="product-subtitle">Ready Stock (3% GST Paid)</div>
                </div>
              </div>
            </div>
            <div className="col-buy">
              <div className="price-value">{rates.silverBank.buy}</div>
              <div className="price-subtitle">Low: {rates.silverBank.low}</div>
            </div>
            <div className="col-sell">
              <div className="price-value sell-price">{rates.silverBank.sell}</div>
              <div className="price-subtitle">High: {rates.silverBank.high}</div>
            </div>
            <div className="col-change">
              <div className="change-value" style={{ color: getChangeColor(rates.silverBank.change) }}>
                {rates.silverBank.change}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Future Rates Section */}
      <div className="future-rates-section">
        <div className="future-rates-grid">
          <div className="future-rate-card">
            <div className="future-header">
              <div className="future-icon">Au</div>
              <h3>GOLD FUTURE</h3>
            </div>
            <div className="future-prices">
              <div className="price-row">
                <span className="price-label">BID</span>
                <span className="price-value">{rates.goldFuture.bid}</span>
                <span className="price-high">H: {rates.goldFuture.high}</span>
              </div>
              <div className="price-row">
                <span className="price-label">ASK</span>
                <span className="price-value ask-price" style={{ color: '#fff' }}>{rates.goldFuture.ask}</span>
                <span className="price-low">L: {rates.goldFuture.low}</span>
              </div>
            </div>
          </div>

          <div className="future-rate-card">
            <div className="future-header">
              <div className="future-icon silver">Ag</div>
              <h3>SILVER FUTURE</h3>
            </div>
            <div className="future-prices">
              <div className="price-row">
                <span className="price-label">BID</span>
                <span className="price-value">{rates.silverFuture.bid}</span>
                <span className="price-high">H: {rates.silverFuture.high}</span>
              </div>
              <div className="price-row">
                <span className="price-label">ASK</span>
                <span className="price-value ask-price" style={{ color: '#fff' }}>{rates.silverFuture.ask}</span>
                <span className="price-low">L: {rates.silverFuture.low}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="rates-disclaimer">
        <p>Rates are indicative and subject to change. All prices are in Indian Rupees (INR).</p>
      </div>
    </div>
  );
};

export default LiveGoldRate;
